import FootballIcon from './icons/FootballIcon'
import { surname } from '../playerNames'
import type { MatchEvent } from '../types'
import './FixtureGoals.css'

interface FixtureGoalsProps {
  /** Goal events for one fixture, chronological, both sides mixed. */
  goals: MatchEvent[]
}

/** A scorer and every goal they scored, in the order they scored them. */
interface ScorerLine {
  player: string
  minutes: number[]
  assists: string[]
}

/**
 * One line per scorer, not one per goal: a brace reads `Ivers 10', 51'` rather than twice down the
 * column. Grouped by name in the order each scorer first appears, so the block still reads
 * chronologically from the top.
 */
function byScorer(goals: MatchEvent[]): ScorerLine[] {
  const lines: ScorerLine[] = []
  for (const goal of goals) {
    const existing = lines.find((line) => line.player === goal.player)
    const line = existing ?? { player: goal.player, minutes: [], assists: [] }
    line.minutes.push(goal.minute)
    if (goal.assistBy) line.assists.push(goal.assistBy)
    if (!existing) lines.push(line)
  }
  return lines
}

/**
 * The goals on a collapsed fixture row: scorers, minutes, and the assists where there are any.
 *
 * Not the expanded timeline's layout, and deliberately so (design reference
 * `docs/compactrefffixture.png`). The timeline pairs every event with its own minute against a
 * centre rail; here the minute rides beside the name and **one** ball marks the whole block, so a
 * five-goal match costs three lines rather than five rails' worth of vertical space.
 *
 * Each side is its own list, read top-down in the order its goals were scored. The two columns are
 * not rows: a team's second goal sits under its first, never beside the other team's.
 *
 * On a scorer with more than one goal the assists collapse to a single list under the name, in
 * scored order, without minutes. Which assist went with which goal is left to that order; the
 * expanded timeline pairs them exactly.
 */
function FixtureGoals({ goals }: FixtureGoalsProps) {
  const scored = goals.filter((goal) => !goal.ownGoal)
  const ownGoals = goals.filter((goal) => goal.ownGoal)
  const home = byScorer(scored.filter((goal) => goal.team === 'home'))
  const away = byScorer(scored.filter((goal) => goal.team === 'away'))

  return (
    <div className="fixture-goals">
      {/* One ball for every goal that went in at the right end — it says "these are goals", which
          does not need saying once per scorer. Absent entirely when the only goals are own ones. */}
      {(home.length > 0 || away.length > 0) && (
        <>
          <div className="fixture-goals__side fixture-goals__side--home">
            {home.map((line) => (
              <ScorerCell key={line.player} line={line} />
            ))}
          </div>
          <span className="fixture-goals__icon" aria-hidden="true">
            <FootballIcon />
          </span>
          <div className="fixture-goals__side fixture-goals__side--away">
            {away.map((line) => (
              <ScorerCell key={line.player} line={line} />
            ))}
          </div>
        </>
      )}

      {/* An own goal gets a row of its own with its own red ball. The block's shared ball above
          cannot say which of several goals went in at the wrong end. It sits on the side that
          *gained* the goal, the way a scoreline is read everywhere else — the red ball and the
          (OG) are what say the scorer plays for the other lot. */}
      {ownGoals.map((goal, index) => (
        <OwnGoalRow key={index} goal={goal} />
      ))}
    </div>
  )
}

function ScorerCell({ line }: { line: ScorerLine }) {
  return (
    <span className="fixture-goals__goal">
      <span className="fixture-goals__scorer">
        {surname(line.player)} {line.minutes.map((minute) => `${minute}'`).join(', ')}
      </span>
      {line.assists.length > 0 && (
        <span className="fixture-goals__assist">
          ass. {line.assists.map(surname).join(', ')}
        </span>
      )}
    </span>
  )
}

function OwnGoalRow({ goal }: { goal: MatchEvent }) {
  const cell = (
    <span className="fixture-goals__goal">
      {/* "(OG)" in words as well as the red ball: colour alone is not a tell anyone should have to
          learn, and this is the notation every scoreline already uses. */}
      <span className="fixture-goals__scorer">
        {surname(goal.player)} {goal.minute}&apos; (OG)
      </span>
    </span>
  )
  return (
    <>
      <div className="fixture-goals__side fixture-goals__side--home">
        {goal.team === 'home' && cell}
      </div>
      <span
        className="fixture-goals__icon fixture-goals__icon--own-goal"
        aria-label={goal.note ?? 'Own goal'}
      >
        <FootballIcon />
      </span>
      <div className="fixture-goals__side fixture-goals__side--away">
        {goal.team === 'away' && cell}
      </div>
    </>
  )
}

export default FixtureGoals
